import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { config } from '../../utils/config';
import { trackPurchaseInitiated } from '../../utils/analytics';
import './SingleReportPurchase.css';

interface Product {
  id: string;
  name: string;
  description: string;
  type: 'subscription' | 'one_time';
  credits?: number;
  price_cad: number;
  stripe_price_id: string;
}

interface SingleReportPurchaseProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const SingleReportPurchase: React.FC<SingleReportPurchaseProps> = ({ 
  onSuccess, 
  onError 
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, session } = useAuth();
  const navigate = useNavigate();

  // Load products to get the correct single report product
  useEffect(() => {
    const loadProducts = async () => {
      try {
        const response = await fetch(`${config.API_URL}/api/payments-simple/products`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json'
          }
        });

        if (!response.ok) {
          throw new Error('Failed to load products');
        }

        const data = await response.json();
        setProducts(data.products);
      } catch (error: any) {
        console.error('Error loading products:', error);
        onError?.(error.message || 'Failed to load pricing information');
      } finally {
        setLoading(false);
      }
    };

    loadProducts();
  }, [onError]);

  const handlePurchase = async () => {
    if (!user || !session) {
      console.log('❌ Auth check failed:', { user: !!user, session: !!session });
      // For anonymous users, show a modal or redirect to login
      const currentPath = window.location.pathname;
      navigate(`/login?redirect=${encodeURIComponent(currentPath)}`);
      return;
    }

    console.log('🔍 Making purchase request:', {
      userId: user.id,
      hasAccessToken: !!session.access_token,
      tokenLength: session.access_token?.length
    });

    // Find the single report product (one_time type)
    const singleReportProduct = products.find(p => p.type === 'one_time');
    if (!singleReportProduct) {
      onError?.('Single report product not available');
      return;
    }

    // Track purchase initiation
    trackPurchaseInitiated('single_report', singleReportProduct.price_cad);

    setIsLoading(true);
    
    try {
      // Use the existing payment system from the pricing page
      const response = await fetch(`${config.API_URL}/api/payments-simple/create-checkout-session`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ 
          productId: singleReportProduct.id,
          priceId: singleReportProduct.stripe_price_id
        })
      });

      const data = await response.json();

      console.log('🔍 Purchase response:', {
        status: response.status,
        ok: response.ok,
        data: data
      });

      if (!response.ok) {
        if (response.status === 401) {
          console.log('❌ 401 Error - Authentication failed');
          onError?.('Authentication required. Please sign in again.');
        } else {
          console.log('❌ Other error:', data.error);
          onError?.(data.error || 'Failed to create checkout session');
        }
        return;
      }

      if (data.url) {
        // Redirect to Stripe checkout
        window.location.href = data.url;
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error: any) {
      console.error('Payment error:', error);
      onError?.('Payment failed. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Find the single report product
  const singleReportProduct = products.find(p => p.type === 'one_time');

  if (loading) {
    return (
      <div className="single-report-purchase">
        <div className="loading-spinner">Loading...</div>
      </div>
    );
  }

  if (!singleReportProduct) {
    return (
      <div className="single-report-purchase">
        <p className="error-message">Single report product not available</p>
      </div>
    );
  }

  return (
    <>
      <div className="purchase-card-header">
        <h3 className="purchase-card-title">Single Report Purchase</h3>
        <div className="purchase-card-price">$49 CAD</div>
        <p className="purchase-card-period">one-time</p>
        <div className="pricing-note">
          <span className="asterisk">*</span> Limited post-launch price
        </div>
      </div>

      <div className="purchase-card-content">
        <p className="purchase-card-description">
          {singleReportProduct.description}
        </p>
        
        <ul className="purchase-card-features">
          <li>✓ Complete zoning analysis</li>
          <li>✓ Development potential assessment</li>
          <li>✓ Parking requirements</li>
          <li>✓ Setback requirements</li>
          <li>✓ Heritage considerations</li>
        </ul>
      </div>
      
      <div className="purchase-card-footer">
        <button 
          className="purchase-card-button" 
          onClick={handlePurchase}
          disabled={isLoading}
        >
          {isLoading ? 'Processing...' : 'Purchase Report'}
        </button>
        {!user && (
          <p className="login-required">Please sign in to purchase</p>
        )}
      </div>
    </>
  );
};

export default SingleReportPurchase;
